import React, { memo, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { NavItemsContext } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/controllers/context.tsx';
import { getConfigurableMenuIds } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/get-configurable-menu-ids/index.tsx';
import {
	getAppItemsData,
	getProductItemsData,
	getVisibleAppItemsData,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/items-data/index.tsx';
import type { ConfigurableMenuItems$key } from '@atlassian/jira-relay/src/__generated__/ConfigurableMenuItems.graphql';
import { GlobalAppShortcuts } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-global-app-shortcuts/src/index.tsx';
import { useGlobalApps } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/global-apps/index.tsx';
import { TopLevelSpacer } from '@atlassian/navigation-system';
import { fg } from '@atlassian/jira-feature-gating';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { ConfigurableMenuItem } from '../../common/ui/configurable-menu-item/ConfigurableMenuItem.tsx';
import { useFilterL1MenuItemsByEditionAndLicense } from '../../controllers/use-filter-l1-menu-items-by-edition-and-license/index.tsx';
import { useRefreshL1MenuItems } from '../../controllers/use-refresh-l1-menu-items/index.tsx';
import { MoreMenuIsOpenContextProvider } from './more-menu-is-open-context-provider/index.tsx';
import { SidebarCustomization } from './sidebar-customization/SidebarCustomization.tsx';
import { PeekMenuIdContextProvider } from './peek-menu-id-context-provider/index.tsx';
import {
	GlobalShortcuts,
	GlobalShortcutsWithInvitePeopleProvider,
} from './global-shortcuts/index.tsx';

export type ConfigurableMenuItemsProps = {
	menuData: ConfigurableMenuItems$key;
};

export const ConfigurableMenuItems = componentWithFG(
	'jfp-magma-stability-check-nav4-batch-1',
	memo(ConfigurableMenuItemsInner),
	ConfigurableMenuItemsInner,
);

function ConfigurableMenuItemsInner({ menuData }: ConfigurableMenuItemsProps) {
	const data = useFragment<ConfigurableMenuItems$key>(
		graphql`
			fragment ConfigurableMenuItems on Query {
				jira @optIn(to: "JiraUserNavConfig") {
					userNavigationConfiguration(cloudID: $cloudId, navKey: "NAV_SIDEBAR") {
						navItems {
							# eslint-disable-next-line @atlassian/relay/unused-fields
							menuId @required(action: THROW)
							# eslint-disable-next-line @atlassian/relay/unused-fields
							isVisible @required(action: THROW)
						}
					}
				}
				...ConfigurableMenuItem
			}
		`,
		menuData,
	);

	const globalAppLicense = useGlobalApps();

	const navItems = useFilterL1MenuItemsByEditionAndLicense(
		// Add back any missing menu ids before filtering in case licensing has changed or a new L1 has been created.
		// This also handles when a user has no configuration yet.
		useRefreshL1MenuItems(data.jira?.userNavigationConfiguration?.navItems),
	);

	const navItemsContext = useMemo(
		() => ({
			navItems,
			visibleMenuIds: getConfigurableMenuIds(navItems, true),
			moreMenuIds: getConfigurableMenuIds(navItems, false),
			productItemsData: getProductItemsData(navItems),
		}),
		[navItems],
	);

	const visibleGlobalAppMenuIds = fg('jfp-magma-stability-check-nav4-batch-1')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useMemo(
				() => getVisibleAppItemsData(navItems, globalAppLicense),
				[globalAppLicense, navItems],
			)
		: getVisibleAppItemsData(navItems, globalAppLicense);
	const appItemsData = fg('jfp-magma-stability-check-nav4-batch-1')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useMemo(() => getAppItemsData(navItems, globalAppLicense), [globalAppLicense, navItems])
		: getAppItemsData(navItems, globalAppLicense);

	// Teams App requires the invite modal context provider to be wrapped around it.
	const globalShortcuts = useMemo(() => {
		if (fg('nav4_global_links_jira')) {
			return (
				<>
					<TopLevelSpacer />
					{fg('nav4_gas_include_teams') ? (
						<GlobalShortcutsWithInvitePeopleProvider appItemsData={appItemsData} />
					) : (
						<GlobalShortcuts appItemsData={appItemsData} />
					)}
				</>
			);
		}

		return visibleGlobalAppMenuIds.length > 0 ? (
			<>
				<TopLevelSpacer />
				{visibleGlobalAppMenuIds.map((menuId) => (
					<GlobalAppShortcuts menuId={menuId} key={menuId} />
				))}
			</>
		) : null;
	}, [visibleGlobalAppMenuIds, appItemsData]);

	return (
		<NavItemsContext.Provider value={navItemsContext}>
			<PeekMenuIdContextProvider>
				<MoreMenuIsOpenContextProvider>
					{navItemsContext.visibleMenuIds.map((menuId) => (
						<ConfigurableMenuItem key={menuId} menuData={data} menuId={menuId} />
					))}
					{globalShortcuts}
					{fg('blu-6576_migrate_to_platform_customisation') && <TopLevelSpacer />}
					<SidebarCustomization
						navItems={navItems}
						menuData={data}
						productItemsData={navItemsContext.productItemsData}
						appItemsData={appItemsData}
					/>
				</MoreMenuIsOpenContextProvider>
			</PeekMenuIdContextProvider>
		</NavItemsContext.Provider>
	);
}
